import { AnimatePresence, motion, MotionConfig, type Transition, type Variants } from 'framer-motion'
import type { ReactNode } from 'react'
import { useAtomValue } from 'jotai'
import { useHasLiveAudioNote } from '@renderer/hooks/audio/use-audio-note-selectors'
import { BusyPill } from './busy-pill'
import { ActionViewControlRow } from './control-row'
import type { ActionViewChatControl } from './chat-control'
import { ActionViewHostedFeedback } from './hosted-feedback'
import { ActionViewDirtyControls } from './dirty-controls'
import { PendingAnswerableCard } from '../../../components/pending-answerable-card'
import { getPendingQuestions } from '../../../hooks/staged-actions/prerequisites'
import { ActionQuestionsCard } from './action-questions-card'
import { useViewScope } from '../../../view-state-atoms'
import { useOpenActionChatDrawer } from '../use-action-chat-drawer'
import { SidepanelMeetingCluster } from '../../../components/sidepanel/nested/meeting-cluster'
import { useActionViewModel, type ActionViewModel } from '../use-action-view-model'
import { useIsActionHeld } from '../use-action-lock'

/**
 * Which region owns the row. One decision, read by both the slot (does the
 * meeting cluster fit beside it?) and the region (what to render, and what to
 * key the swap on) — derived twice, they would drift.
 *
 * Ready and Sent share `controls` deliberately: they're the same toolbar before
 * and after execution, so sending morphs the two controls that change rather
 * than tearing the row down (HIG-5831).
 */
type FloatingRegionKind = 'updating' | 'question' | 'feedback' | 'dirty' | 'questions' | 'controls'

/** `questions` sits below `dirty`, so a field filled in by hand on an action still asking can be saved. */
function resolveFloatingRegionKind({ action, state, isDirty, isEditable }: ActionViewModel): FloatingRegionKind {
  if (state === 'updating') return 'updating'
  if (state === 'question') return 'question'
  if (state === 'feedback') return 'feedback'
  if (state === 'ready' && isDirty) return 'dirty'
  if (state === 'ready' && isEditable && getPendingQuestions(action)) return 'questions'
  return 'controls'
}

export function ActionViewInlineControls({
  model,
  enableHotkeys = false,
  showHotkeys = enableHotkeys,
  minimalSentControls = false,
}: {
  model: ActionViewModel
  enableHotkeys?: boolean
  /** Keep the hotkey chips rendered (e.g. through a collapse animation) while the binding is off. */
  showHotkeys?: boolean
  /** Output cards keep completed actions read-only and expose only their result controls. */
  minimalSentControls?: boolean
}) {
  const isLocked = useIsActionHeld(model.action)
  if (!model.action || isLocked) {
    return null
  }

  return (
    <ActionViewFloatingRegion
      model={model}
      kind={resolveFloatingRegionKind(model)}
      enableHotkeys={enableHotkeys}
      showHotkeys={showHotkeys}
      minimalSentControls={minimalSentControls}
    />
  )
}

/**
 * Floating-bar slot for the Action View: resolves the model for the open
 * action and renders the state's footer region. Mounted by
 * `SidepanelFloatingBar` while the Action View is the top view.
 *
 * While a meeting is live the recording controls take the left of the row and
 * the action's controls compact around them (Figma 8004:32399) — but only
 * against the control row. The other three regions are a card and two centered
 * clusters that need the whole 384px, and the recording is reachable from the
 * Meeting View for the seconds they last.
 *
 * The cluster sits outside the region's `AnimatePresence` deliberately: the
 * region swaps wholesale between those states, and a running recording must not
 * blur-drop through every one of them.
 */
export function ActionViewFloatingBarSlot({
  actionId,
  braintrustLink,
  showAskUserCards = true,
  onDraftSaved,
  chatOpen = false,
  onOpenChat,
  applyingChanges = false,
}: {
  actionId: string | undefined
  /** Ready-made permalink from the opener (the history feed serves one). */
  braintrustLink?: string
  showAskUserCards?: boolean
  /** Fires after the dirty region's Save persists (see ActionViewDirtyControls). */
  onDraftSaved?: () => void
  /** Whether the host's chat drawer is open — the chat circle's pressed state. */
  chatOpen?: boolean
  /** Host-owned drawer wiring for the chat circle; defaults to the action entry's own drawer. */
  onOpenChat?: () => void
  /** A turn is in flight in that chat, so the circle widens into a status pill. */
  applyingChanges?: boolean
}) {
  const model = useActionViewModel(actionId)
  const hasLiveMeeting = useHasLiveAudioNote()
  const openActionDrawer = useOpenActionChatDrawer()
  // The circle needs a thread to open: a host hands in its own drawer wiring
  // (the chat output), otherwise the action entry's drawer serves — which only
  // exists once the action has a conversation.
  const chat: ActionViewChatControl | undefined = onOpenChat
    ? { open: chatOpen, onOpen: onOpenChat, applyingChanges }
    : model.chatId
      ? { open: chatOpen, onOpen: openActionDrawer, applyingChanges }
      : undefined
  // While the leave dialog holds the view, the bar's controls step aside — the
  // dialog carries the same Save/Discard pair, and two save surfaces at once
  // read as two different questions.
  const isLeaveDialogOpen = useAtomValue(useViewScope().pendingLeave) !== null
  // A locked preview's only ways on live in its body: upgrade or dismiss.
  const isLocked = useIsActionHeld(model.action)
  if (model.isLoading) {
    return null
  }
  // This row keeps rendering with the chat drawer open, and is simply covered
  // by it: the sheet paints one layer above the floating bar (SIDEPANEL_LAYER_Z)
  // because these controls belong to the action *behind* the sheet, and floating
  // them on top of the thread reads as the sheet having failed to open. Left
  // mounted rather than suppressed so the row is already in its right state
  // under the sheet's exit slide instead of popping in after it.
  const resolvedKind = model.action && !isLeaveDialogOpen && !isLocked ? resolveFloatingRegionKind(model) : null
  const kind = !showAskUserCards && (resolvedKind === 'question' || resolvedKind === 'feedback') ? null : resolvedKind
  // Only a live meeting compacts the row. Without one the action owns the whole
  // bar and the full toolbar fits (Figma 7990:28196) — folding it into `…` there
  // hides the feedback pair, archive and copy for no reason.
  const isCompact = hasLiveMeeting && kind === 'controls'
  // With no action left to render (it was archived out from under the open
  // view) the cluster holds the row alone — the bar has handed the pill to this
  // slot, so bailing out here would leave a recording with no controls at all.
  const showCluster = hasLiveMeeting && (kind === 'controls' || kind === null)
  return (
    <div className="flex min-w-0 flex-1 items-end gap-2">
      {/* No timer here: the pill sheds it to leave room for the action's CTA. */}
      {showCluster && <SidepanelMeetingCluster showElapsedTime={false} />}
      <ActionViewFloatingRegion
        model={model}
        kind={kind}
        braintrustLink={braintrustLink}
        chat={chat}
        isCompact={isCompact}
        onDraftSaved={onDraftSaved}
      />
    </div>
  )
}

// Region swap choreography: the outgoing region blur-drops toward the bottom
// edge and the incoming one rises from it. Exit runs snappier than entry.
const regionVariants: Variants = {
  initial: { opacity: 0, y: 14, scale: 0.985, filter: 'blur(4px)' },
  animate: { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' },
  exit: {
    opacity: 0,
    y: 14,
    scale: 0.985,
    filter: 'blur(4px)',
    transition: { duration: 0.14, ease: 'easeOut' },
  },
}

const regionTransition: Transition = { duration: 0.18, ease: 'easeOut' }

/**
 * The region swap choreography, shared with the chat output's floating bar. A
 * key change swaps regions wholesale; a null key keeps the presence mounted so
 * the outgoing region still gets its exit.
 */
export function FloatingBarRegionSwap({
  regionKey,
  children,
  animateOnMount = false,
}: {
  regionKey: string | null
  children: ReactNode
  animateOnMount?: boolean
}) {
  return (
    <MotionConfig reducedMotion="user">
      <AnimatePresence mode="popLayout" initial={animateOnMount}>
        {regionKey !== null && (
          <motion.div
            key={regionKey}
            variants={regionVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={regionTransition}
            className="flex min-w-0 flex-1 items-end"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </MotionConfig>
  )
}

/**
 * The Action View's footer region, mounted in the sidepanel floating bar (the
 * row above the chat input). Updating collapses to the centered busy pill,
 * Question replaces the row with the question card, and unsent inline edits
 * swap it for the centered discard + "Save changes" pair (HIG-5830) — each a
 * different job, so each gets a full region swap.
 */
function ActionViewFloatingRegion({
  model,
  kind,
  braintrustLink,
  isCompact = false,
  enableHotkeys = true,
  showHotkeys = enableHotkeys,
  minimalSentControls = false,
  onDraftSaved,
  chat,
}: {
  model: ActionViewModel
  /** Resolved by the slot, which needs the same answer to place the meeting cluster. */
  kind: FloatingRegionKind | null
  braintrustLink?: string
  /** The control row's chat circle — slot-driven hosts only, so inline rows don't grow one. */
  chat?: ActionViewChatControl
  /** The meeting cluster holds the left of the row, so the controls fold into `…`. */
  isCompact?: boolean
  /** Disable view-wide shortcuts when multiple action controls share a view. */
  enableHotkeys?: boolean
  /** Keep the hotkey chips rendered while the binding is off. */
  showHotkeys?: boolean
  minimalSentControls?: boolean
  onDraftSaved?: () => void
}) {
  const { action, state, editState } = model

  if (!action) {
    return null
  }
  const questions = kind === 'questions' ? getPendingQuestions(action) : null

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      {/* Outside the presence, so a swap to dirty/updating — or the leave
          dialog nulling `kind` — cannot unmount a half-typed answer. */}
      <ActionViewHostedFeedback action={action} />

      <FloatingBarRegionSwap regionKey={kind}>
        {kind === 'updating' ? (
          <BusyPill label="Applying your feedback…" />
        ) : kind === 'question' || kind === 'feedback' ? (
          <PendingAnswerableCard action={action} source="action-view" />
        ) : questions ? (
          <ActionQuestionsCard actionId={action.id} questions={questions} />
        ) : kind === 'dirty' ? (
          <ActionViewDirtyControls
            action={action}
            draft={model.draft}
            enableHotkey={enableHotkeys}
            onSaved={onDraftSaved}
          />
        ) : (
          // Keyed by action: the bar stays mounted across actions, and one
          // action's in-flight invoke must not leave its spinner on the next.
          <ActionViewControlRow
            key={action.id}
            action={action}
            state={state}
            editState={editState}
            braintrustLink={braintrustLink}
            chat={chat}
            isCompact={isCompact}
            enableHotkeys={enableHotkeys}
            showHotkeys={showHotkeys}
            minimalSentControls={minimalSentControls}
          />
        )}
      </FloatingBarRegionSwap>
    </div>
  )
}
